<script setup lang="ts">
import { useVirtualList } from '@vueuse/core';
import { computed, toRef } from 'vue';

import type { Coinsured } from "@/api/contracts/types";

interface Props {
  coinsuredPersons: Coinsured[];
}

const props = defineProps<Props>();

// Contracts of large company clients list thousands of co-insured persons. Above this count only
// the visible rows are rendered.
const VIRTUALIZE_FROM = 50;

// Name plus relation line. `useVirtualList` computes the scroll height from it, so every row has
// to have exactly this height.
const ITEM_HEIGHT = 44;

const virtualize = computed(() => props.coinsuredPersons.length > VIRTUALIZE_FROM);

const { list, containerProps, wrapperProps } = useVirtualList(
  toRef(() => props.coinsuredPersons),
  { itemHeight: ITEM_HEIGHT },
);
</script>

<template>
  <div
    v-if="virtualize"
    v-bind="containerProps"
    class="max-h-96 w-full"
    data-testid="coinsured-virtual-list"
  >
    <div v-bind="wrapperProps">
      <div
        v-for="{ data: person } in list"
        :key="person.id"
        class="flex flex-col items-end justify-center"
        :style="{ height: `${ITEM_HEIGHT}px` }"
        data-testid="coinsured-person"
      >
        <span class="font-medium text-gray-900 text-right truncate max-w-full">{{ person.firstName }} {{ person.lastName }}</span>
        <span class="text-muted text-xs">{{ person.relation }}</span>
      </div>
    </div>
  </div>

  <div
    v-else
    class="flex flex-col gap-1 items-end"
  >
    <div
      v-for="person in coinsuredPersons"
      :key="person.id"
      class="flex flex-col items-end"
      data-testid="coinsured-person"
    >
      <span class="font-medium text-gray-900 text-right">{{ person.firstName }} {{ person.lastName }}</span>
      <span class="text-muted text-xs">{{ person.relation }}</span>
    </div>
  </div>
</template>
